import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, onSnapshot } from 'firebase/firestore';
import SidePanel from './SidePanel';
import StoreLocator from './StoreLocator';
import OrdersView from './OrdersView';
import AnimatedHamburger from './AnimatedHamburger';
import Messages from './Messages';
import Footer from './layouts/Footer';

const WorkerDashboard = ({ onLogout, isWorker = true }) => {
  const [orders, setOrders] = useState([]);
  const [currentView, setCurrentView] = useState('orders'); // Default to 'orders'
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const fetchOrders = () => {
    const db = getFirestore();
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newOrders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(newOrders);
    });

    return unsubscribe;
  };

  useEffect(() => {
    const unsubscribe = fetchOrders();
    return () => unsubscribe();
  }, []);

  const handleViewChange = (view) => {
    setCurrentView(view);
    setIsSidePanelOpen(false); // Close the side panel when changing views
  };

  const renderContent = () => {
    console.log("Rendering content, currentView:", currentView); // Debug log
  
    switch (currentView) {
      case 'orders':
        return <OrdersView orders={orders} isWorker={true} fetchOrders={fetchOrders} />;
      case 'storeLocator':
        return (
          <div className="mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold mb-4">Store Locator</h2>
            <StoreLocator />
          </div>
        );
      case 'messages':
        console.log("Rendering messages view"); // Debug log
        return (
          <div className="mb-8">
            <h2 className="text-xl sm:text-2xl font-extrabold mb-4 bg-gradient-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
              Messages
            </h2>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <Messages 
                isAdmin={false} 
                isWorker={true}
                key="worker-messages"
              />
              {/* Add debug element */}
              <div className="hidden">Debug: Messages Component Mounted</div>
            </div>
          </div>
        );
      default:
        console.log("No matching view found"); // Debug log
        return null;
    }
  };

  return (
    <div className="min-h-screen pb-4 relative overflow-hidden">
      {/* Background blobs */}
      <div className="fixed -z-10 top-0 -left-4 w-96 h-96 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
      <div className="fixed -z-10 top-1/3 right-4 w-96 h-96 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="fixed -z-10 bottom-0 left-1/3 w-96 h-96 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

      <header className="fixed top-0 left-0 right-0 bg-white z-50" style={{
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.1), 0 2px 4px -1px rgba(16, 185, 129, 0.1)'
        }}>
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            <AnimatedHamburger isOpen={isSidePanelOpen} toggle={() => setIsSidePanelOpen(!isSidePanelOpen)} />
            <div className="flex flex-col items-center"> {/* Add this wrapper div */}
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-wider bg-gradient-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
                CNYC
              </h1>
              <span className="text-sm font-semibold text-gray-600">Worker Dashboard</span>
            </div>
            <div className="w-8"></div> {/* This creates balance with the hamburger menu */}
          </div>
        </header>

      <SidePanel 
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        onLogout={onLogout}
        onOpenStoreLocator={() => handleViewChange('storeLocator')}
        onOpenOrders={() => handleViewChange('orders')}
        onViewChange={handleViewChange} 
        activeView={currentView}
        isWorker={true}  // Just pass true directly here
        currentView={currentView}
      />

      <main className="container mx-auto px-4 mt-24 sm:mt-28">
        {currentView === 'orders' ? (
          <div className="mb-8">
            <h2 className="text-2xl font-extrabold mb-4 bg-gradient-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
              Orders
            </h2>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <OrdersView 
                orders={orders} 
                isWorker={true}  // Just pass true directly here
                fetchOrders={fetchOrders}
              />
            </div>
          </div>
        ) : currentView === 'storeLocator' ? (
          <div className="mb-8">
            <h2 className="text-2xl font-extrabold mb-4 bg-gradient-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
              Store Locator
            </h2>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <StoreLocator isAdmin={false} />
            </div>
          </div>
        ) : currentView === 'messages' ? (
          <div className="mb-8">
            <h2 className="text-2xl font-extrabold mb-4 bg-gradient-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
              Messages
            </h2>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <Messages 
                isAdmin={false} 
                isWorker={true}  // Just pass true directly here
                key="worker-messages"
              />
            </div>
          </div>
        ) : null}
      </main>
      <Footer/>
    </div>
  );
};

export default WorkerDashboard;